import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useState, useEffect } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import EventCard from '@/components/EventCard';
import { fetchEvents, fetchCities, fetchOrganizerDirectory, fetchOrganizerOwnEventsPublic } from '@/apiEndpoints';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EmptySearch from '@/components/EmptySearch';
import MobileBottomNav from '@/components/MobileBottomNav';
import { Input } from '@heroui/input';
import SearchIcon from '@/icons/Search';
import Avatar from '@/components/Avatar';
import { useAuth } from '@/contexts/auth-context';
import moment from 'moment';
import { IconBuildingSkyscraper as Building2, IconArrowRight as ArrowRight } from '@tabler/icons-react';
import { wrap, pageBg, displayTitle, sectionTitle, eyebrow, glow, Breadcrumbs, EmptyState } from '@/components/ui/design';

const SECTION_TITLE_CLASSES = sectionTitle;
const HEADING_CLASSES = `${displayTitle} break-words`;

const COUNTRY_CODES: Record<string, string> = {
    'united states': 'US', usa: 'US', 'united states of america': 'US',
    canada: 'CA', 'united kingdom': 'GB', uk: 'GB', mexico: 'MX', australia: 'AU',
};

function resolveCountry(raw?: string): { name: string; flag: string } {
    const value = (raw || '').trim();
    if (!value) return { name: 'Other', flag: '' };
    const code = value.length === 2 ? value.toUpperCase() : COUNTRY_CODES[value.toLowerCase()];
    if (!code) return { name: value, flag: '' };
    let name = value;
    try {
        name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || value;
    } catch {
        name = value;
    }
    const flag = String.fromCodePoint(...[...code].map((c) => 127397 + c.charCodeAt(0)));
    return { name, flag };
}

function CityCard({ name, count, onSelect }: { name: string; count: number; onSelect: (city: string) => void }) {
    return (
        <button
            type="button"
            onClick={() => onSelect(name)}
            className="group flex flex-col items-start gap-4 rounded-[28px] bg-grey99 p-5 text-left ring-1 ring-black/[0.04] transition-all duration-300 hover:-translate-y-1 hover:bg-grey95 dark:bg-white/[0.04] dark:ring-white/[0.06] dark:hover:bg-white/[0.08]"
        >
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-nightAccent/15 dark:text-nightAccent">
                <Building2 className="h-5 w-5" />
            </span>
            <span className="w-full truncate text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white">{name}</span>
            <span className="-mt-3 text-sm text-grey40 dark:text-nightMuted">
                <span className="font-bold">{count}</span> {count === 1 ? 'Event' : 'Events'}
            </span>
        </button>
    );
}

function OrganizerCard({ org, onSelect }: { org: any; onSelect: (id: string) => void }) {
    return (
        <button
            type="button"
            onClick={() => onSelect(org._id)}
            className="group flex items-center gap-4 rounded-[28px] bg-grey99 p-4 text-left ring-1 ring-black/[0.04] transition-all duration-300 hover:-translate-y-1 hover:bg-grey95 dark:bg-white/[0.04] dark:ring-white/[0.06] dark:hover:bg-white/[0.08]"
        >
            <img
                src={org.attachments?.avatar?.link || '/host-avatar.png'}
                alt={org.orgName}
                onError={(e) => { (e.target as HTMLImageElement).src = '/host-avatar.png'; }}
                className="h-16 w-16 flex-shrink-0 rounded-full bg-white object-cover ring-1 ring-black/[0.06] dark:ring-white/10"
            />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-sm text-grey40 dark:text-nightMuted">
                <span className="truncate text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white">{org.orgName}</span>
                <span>{formatCount(org.followCount || 0)} Followers</span>
                <OrganizerUpcomingCount organizerId={org._id} />
            </span>
            <ArrowRight className="h-5 w-5 flex-shrink-0 text-grey60 transition-transform group-hover:translate-x-0.5 dark:text-nightMuted" />
        </button>
    );
}

function CitiesBrowse({ onSelectCity }: { onSelectCity: (city: string) => void }) {
    const { data: cities, isLoading } = useQuery({
        queryKey: ['browseCitiesGrouped'],
        queryFn: async () => {
            const res = await fetchCities();
            return (res || []).filter((c: any) => c.name || c.city);
        },
        staleTime: 15 * 60 * 1000,
    });

    const groups = new Map<string, { name: string; flag: string; cities: { name: string; count: number }[] }>();
    (cities || []).forEach((c: any) => {
        const country = resolveCountry(c.country);
        const group = groups.get(country.name) || { ...country, cities: [] };
        const name = c.name || c.city;
        const existing = group.cities.find((x) => x.name === name);
        if (existing) existing.count += c.eventCount || 0;
        else group.cities.push({ name, count: c.eventCount || 0 });
        groups.set(country.name, group);
    });

    return (
        <div className={`${wrap} mt-6 mb-16 sm:mt-10`}>
            <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Cities' }]} />
            <h1 className={`${HEADING_CLASSES} mt-6 mb-10 sm:mb-14`}>Cities</h1>
            {isLoading ? (
                <div className="text-grey40 dark:text-nightMuted">Loading cities…</div>
            ) : (
                [...groups.values()].map((group) => (
                    <section key={group.name} className="mb-12">
                        <h2 className={`${SECTION_TITLE_CLASSES} mb-6 flex items-center gap-3`}>
                            {group.flag && <span>{group.flag}</span>}
                            {group.name}
                        </h2>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
                            {group.cities.map((city) => (
                                <CityCard key={city.name} name={city.name} count={city.count} onSelect={onSelectCity} />
                            ))}
                        </div>
                    </section>
                ))
            )}
        </div>
    );
}

function formatCount(n: number) {
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, '')}k`;
    return String(n);
}

function OrganizerUpcomingCount({ organizerId }: { organizerId: string }) {
    const { data } = useQuery({
        queryKey: ['hostEvents', organizerId],
        queryFn: () => fetchOrganizerOwnEventsPublic(organizerId),
        staleTime: 5 * 60 * 1000,
    });
    const count = (Array.isArray(data?.data) ? data.data : [])
        .filter((e: any) => moment(e.times?.[0]?.startDate).isAfter(moment())).length;
    if (!count) return null;
    return <span><span className="font-bold">{count}</span> {count === 1 ? 'Event' : 'Events'}</span>;
}

function OrganizersBrowse({ onSelectOrganizer }: { onSelectOrganizer: (id: string) => void }) {
    const { data, isLoading } = useQuery({
        queryKey: ['organizerDirectory'],
        queryFn: fetchOrganizerDirectory,
        staleTime: 15 * 60 * 1000,
    });
    const organizers = (data?.data || []).filter((org: any) => !/test/i.test(org.orgName || ''));

    return (
        <div className={`${wrap} mt-6 mb-16 sm:mt-10`}>
            <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Organizations' }]} />
            <h1 className={`${HEADING_CLASSES} mt-6 mb-10 sm:mb-14`}>Organizers</h1>
            {isLoading ? (
                <div className="text-grey40 dark:text-nightMuted">Loading organizers…</div>
            ) : organizers.length === 0 ? (
                <EmptyState title="No organizers found." />
            ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
                    {organizers.map((org: any) => (
                        <OrganizerCard key={org._id} org={org} onSelect={onSelectOrganizer} />
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Search() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, isAuthenticated, logout } = useAuth();
    const isOrganizer = !!user?.organizer;
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const browse = searchParams.get('browse');
    const locationFilter = searchParams.get('location') || undefined;

    // Sample data that matches the image
    const sampleEvents: any[] = [];

    // Get search query from URL
    useEffect(() => {
        const query = searchParams.get('q');
        if (query) {
            setSearchQuery(query);
        }
    }, [searchParams]);

    // Handle like changes
    const handleLikeChange = (eventId: string, likes: number, isLiked: boolean) => {
        console.log(`Event ${eventId} like status changed: ${likes} likes, isLiked: ${isLiked}`);
    };

    // Use react-query's useInfiniteQuery for paginated data
    const {
        data,
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ['search', searchQuery, locationFilter],
        queryFn: async ({ pageParam = 0 }) => {
            const res = await fetchEvents({
                page: pageParam,
                search: searchQuery,
                city: locationFilter,
                date: new Date().toISOString().split('T')[0]
            });
            return res;
        },
        enabled: !browse,
        getNextPageParam: (lastPage) => lastPage.nextPage,
        staleTime: 1000 * 60,
        initialPageParam: 0,
    });

    // Flatten all loaded pages
    const apiEvents = data
        ? data.pages.flatMap(page => Array.isArray(page.data) ? page.data : [])
        : [];
    const userInfos = data
        ? data.pages.flatMap(page => Array.isArray(page.config.userInfos) ? page.config.userInfos : [])
        : [];
    const organizerInfos = data
        ? data.pages.flatMap(page => Array.isArray(page.config.organizerInfos) ? page.config.organizerInfos : [])
        : [];

    const matchesQuery = (value?: string) => !!searchQuery && (value || '').toLowerCase().includes(searchQuery.trim().toLowerCase());
    const isSearching = !!searchQuery.trim();
    const isCityPage = !isSearching && !!locationFilter;

    const { data: allCities } = useQuery({
        queryKey: ['browseCities'],
        queryFn: fetchCities,
        enabled: !browse && isSearching,
        staleTime: 15 * 60 * 1000,
    });
    const { data: orgDirectory } = useQuery({
        queryKey: ['organizerDirectory'],
        queryFn: fetchOrganizerDirectory,
        enabled: !browse && (isSearching || isCityPage),
        staleTime: 15 * 60 * 1000,
    });
    const eventCityNames = new Set(apiEvents.map((e: any) => e.address?.city).filter(Boolean));
    const eventOrganizerIds = new Set(apiEvents.map((e: any) => String(e.organizerId)));
    const cityResults = (allCities || [])
        .map((c: any) => ({ name: c.name || c.city, count: c.eventCount || 0 }))
        .filter((c: any) => c.name && (matchesQuery(c.name) || eventCityNames.has(c.name)))
        .filter((c: any, i: number, arr: any[]) => arr.findIndex(x => x.name === c.name) === i);
    const organizerResults = (orgDirectory?.data || [])
        .filter((o: any) => !/test/i.test(o.orgName || ''))
        .filter((o: any) => (isSearching && matchesQuery(o.orgName)) || eventOrganizerIds.has(String(o._id)));

    return (
        <div className={`min-h-screen flex flex-col overflow-x-clip ${pageBg}`}>
            <div className="hidden md:block">
                <Header />
            </div>

            {/* Mobile Search Bar */}
            <div className={`sm:hidden ${wrap} pt-4 flex items-center gap-2`}>
                <Input
                    className="no-global-input flex-1"
                    startContent={<SearchIcon />}
                    classNames={{
                        input: 'bg-transparent text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted',
                        inputWrapper: 'h-12 rounded-full border-0 bg-grey99 ring-1 ring-black/[0.04] shadow-none data-[hover=true]:bg-grey99 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/[0.06] dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60',
                    }}
                    type="text"
                    variant="bordered"
                    placeholder="Search events, organizers, cities"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                {isAuthenticated ? (
                    <div className="relative flex-shrink-0">
                        <button
                            type="button"
                            aria-label="Profile"
                            onClick={() => setUserMenuOpen((v) => !v)}
                        >
                            <Avatar
                                src={user?.profileImage}
                                name={`${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                                seed={user?.userId}
                                size={36}
                            />
                        </button>
                        {userMenuOpen && (
                            <div className="absolute right-0 top-[44px] mt-2 w-56 rounded-2xl bg-white py-2 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.25)] ring-1 ring-black/[0.06] z-20 dark:bg-nightElevated dark:ring-white/10">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setUserMenuOpen(false);
                                        navigate('/organizations');
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-grey10 hover:bg-grey99 dark:text-white dark:hover:bg-white/[0.06]"
                                >
                                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-grey40 dark:[&_path]:stroke-nightTextSecondary">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M16 11L20 7L16 3M20 7H10M8 21L4 17L8 13M4 17H13" stroke="#354052" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                    {isOrganizer ? 'Switch to Organizing' : "I'm an Organizer"}
                                </button>
                                <hr className="my-2 border-grey95 dark:border-white/10" />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setUserMenuOpen(false);
                                        logout();
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-grey10 hover:bg-grey99 dark:text-white dark:hover:bg-white/[0.06]"
                                >
                                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-grey40 dark:[&_path]:stroke-nightTextSecondary">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M14 8V6C14 5.46957 13.7893 4.96086 13.4142 4.58579C13.0391 4.21071 12.5304 4 12 4H5C4.46957 4 3.96086 4.21071 3.58579 4.58579C3.21071 4.96086 3 5.46957 3 6V18C3 18.5304 3.21071 19.0391 3.58579 19.4142C3.96086 19.7893 4.46957 20 5 20H12C12.5304 20 13.0391 19.7893 13.4142 19.4142C13.7893 19.0391 14 18.5304 14 18V16M7 12H21M18 15L21 12L18 9" stroke="#354052" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <a
                        href="/login"
                        className="flex-shrink-0 flex items-center rounded-full px-4 h-10 text-sm font-semibold bg-grey10 text-white whitespace-nowrap transition-colors hover:bg-black dark:bg-nightAccent dark:text-nightBg"
                    >
                        Login
                    </a>
                )}
            </div>

            {browse === 'cities' && (
                <CitiesBrowse onSelectCity={(city) => navigate(`/search?location=${encodeURIComponent(city)}`)} />
            )}
            {browse === 'organizers' && (
                <OrganizersBrowse onSelectOrganizer={(id) => navigate(`/organization/${id}`)} />
            )}

            {!browse && (
                <>
                    <section className="relative isolate">
                        <div className={glow} />
                        <div className={`${wrap} pt-8 pb-2 sm:pt-14`}>
                            {isCityPage ? (
                                <>
                                    <h1 className={HEADING_CLASSES}>
                                        {locationFilter}
                                    </h1>
                                    <h2 className={`${eyebrow} mt-8 sm:mt-12`}>Events</h2>
                                </>
                            ) : isSearching ? (
                                <>
                                    <h1 className={HEADING_CLASSES}>
                                        Results for <span className="text-grey70 dark:text-white/30">&ldquo;</span>{searchQuery}<span className="text-grey70 dark:text-white/30">&rdquo;</span>
                                    </h1>
                                    <h2 className={`${eyebrow} mt-8 sm:mt-12`}>Events</h2>
                                </>
                            ) : (
                                <h1 className={HEADING_CLASSES}>
                                    Recently Viewed
                                </h1>
                            )}
                        </div>
                    </section>

                    {isLoading ? (
                        <div className={`${wrap} flex justify-center items-center h-40 text-base text-grey40 dark:text-nightMuted`}>
                            <div className="flex flex-col items-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary dark:border-nightAccent mb-3"></div>
                                <span>Loading events...</span>
                            </div>
                        </div>
                    ) : apiEvents.length === 0 ? (
                        <div className={`${wrap} flex-1 mt-6 mb-16`}>
                            <EmptyState
                                title={<span className="flex flex-col items-center"><EmptySearch /><span className="mt-2">Hmm, we couldn't find any events.</span></span>}
                                text="Try other keywords"
                            />
                        </div>
                    ) : (
                        <div className={`${wrap} mt-2 mb-8`}>
                        <div className="grid grid-cols-1 content-start sm:-mx-4 sm:grid-cols-2 lg:grid-cols-3">
                            {apiEvents.map((event, i) => {
                                const userInfo = userInfos.find(x => x.id == event.userId)
                                const organizerInfo = organizerInfos.find(x => x._id == event.organizerId)
                                return (
                                    <EventCard
                                        key={i}
                                        event={event}
                                        userInfo={userInfo}
                                        organizerInfo={organizerInfo}
                                        onLikeChange={handleLikeChange}
                                    />
                                )
                            })}
                        </div>
                        </div>
                    )}

                    {((hasNextPage && !isLoading && apiEvents.length > 0) || (apiEvents.length === 0 && sampleEvents.length > 0)) && (
                        <div className="flex justify-center mb-16">
                            <button
                                className="group inline-flex h-14 items-center gap-3 rounded-full bg-grey10 pl-7 pr-2 text-base font-semibold text-white transition-colors hover:bg-black disabled:opacity-60 dark:bg-white dark:text-nightBg dark:hover:bg-grey90"
                                onClick={() => {
                                    if (apiEvents.length > 0) {
                                        fetchNextPage();
                                    }
                                }}
                                disabled={isFetchingNextPage}
                            >
                                {isFetchingNextPage ? 'Loading...' : 'See More'}
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 transition-transform group-hover:translate-y-0.5 dark:bg-black/10">
                                    <ArrowRight className="h-4 w-4 rotate-90" />
                                </span>
                            </button>
                        </div>
                    )}

                    {isSearching && cityResults.length > 0 && (
                        <section className={`${wrap} mb-16`}>
                            <h2 className={`${SECTION_TITLE_CLASSES} mb-6`}>Cities</h2>
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
                                {cityResults.map((c: any) => (
                                    <CityCard key={c.name} name={c.name} count={c.count} onSelect={(city) => navigate(`/search?location=${encodeURIComponent(city)}`)} />
                                ))}
                            </div>
                        </section>
                    )}

                    {(isSearching || isCityPage) && organizerResults.length > 0 && (
                        <section className={`${wrap} mb-20`}>
                            <h2 className={`${SECTION_TITLE_CLASSES} mb-6`}>Organizers</h2>
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
                                {organizerResults.map((org: any) => (
                                    <OrganizerCard key={org._id} org={org} onSelect={(id) => navigate(`/organization/${id}`)} />
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}

            <Footer hasMobileNav />
            <MobileBottomNav />
        </div>
    );
}
